import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as AlertIcon } from '../../../assets/icons/alert-icon.svg';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SelectField from '../../../shared-components/select-field/SelectField';
import { ISO_COUNTRY_CODES, isValidCountryCode } from '../../../shared-functions/CountryCodes';
import getErrorMessage from '../../../shared-functions/getErrorMessage';
import ocpiApi from '../../../utils/api/ocpi';

interface RegisterPartnerModalProperties {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  ourPartyId: string;
  ourCountryCode: string;
  ourRole: string;
}

/**
 * Validate form inputs based on current mode and return error object
 * @param {any} data - The current form data
 * @param {any} type - Registration mode ('SENDER' or 'RECEIVER')
 * @param {any} t - Translation function
 * @returns {Record<string, string>} Validation errors map
 */
const validateForm = (
  data: {
    theirVersionsUrl: string;
    theirTokenA: string;
    counterPartyId: string;
    counterCountryCode: string;
    counterRole: string;
  },
  type: 'SENDER' | 'RECEIVER',
  t: (key: string) => string,
): Record<string, string> => {
  const validationErrors: Record<string, string> = {};

  const partyId = data.counterPartyId.trim();
  if (!partyId) {
    validationErrors.counterPartyId = t('counterPartyIdRequired');
  } else if (partyId.length !== 3) {
    validationErrors.counterPartyId = t('counterPartyIdLength');
  }

  const countryCode = data.counterCountryCode.trim();
  if (!countryCode) {
    validationErrors.counterCountryCode = t('countryCodeRequired');
  } else if (!isValidCountryCode(countryCode)) {
    validationErrors.counterCountryCode = t('invalidCountryCode');
  }

  if (!data.counterRole) {
    validationErrors.counterRole = t('partnerRoleRequired');
  }

  if (type === 'SENDER') {
    const url = data.theirVersionsUrl.trim();
    if (!url) {
      validationErrors.theirVersionsUrl = t('partnerVersionsUrlRequired');
    } else {
      try {
        const parsedUrl = new URL(url);
        if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
          validationErrors.theirVersionsUrl = t('versionsUrlProtocol');
        }
      } catch {
        validationErrors.theirVersionsUrl = t('invalidVersionsUrl');
      }
    }

    const token = data.theirTokenA.trim();
    if (!token) {
      validationErrors.theirTokenA = t('partnerTokenARequired');
    }
  }

  return validationErrors;
};

/**
 * OCPI Register Partner Modal component
 * @param {any} props - Component props
 * @param {any} props.open - Open state
 * @param {any} props.onClose - Close callback
 * @param {any} props.onSuccess - Success callback
 * @param {any} props.ourPartyId - Our party ID
 * @param {any} props.ourCountryCode - Our country code
 * @param {any} props.ourRole - Our role
 * @returns {any} Rendered component
 */
function RegisterPartnerModal({
  open,
  onClose,
  onSuccess,
  ourPartyId,
  ourCountryCode,
  ourRole,
}: RegisterPartnerModalProperties): React.ReactElement {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('ocpi');
  const [isVersionURLCopied, setIsVersionURLCopied] = useState<boolean>(false);
  const [isTokenACopied, setIsTokenACopied] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [registrationType, setRegistrationType] = useState<'SENDER' | 'RECEIVER'>('SENDER');
  const [generatedData, setGeneratedData] = useState<{
    tokenA: string;
    versionsUrl: string;
  } | null>(null);

  const defaultCounterRole = ourRole.toUpperCase() === 'CPO' ? 'EMSP' : 'CPO';

  const [formData, setFormData] = useState({
    theirVersionsUrl: '',
    theirTokenA: '',
    counterPartyId: '',
    counterCountryCode: '',
    counterRole: defaultCounterRole,
  });

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string | null>(null);

  const isFormValid =
    registrationType === 'SENDER'
      ? !!(
          formData.counterPartyId.trim() &&
          formData.counterCountryCode.trim() &&
          formData.theirVersionsUrl.trim() &&
          formData.theirTokenA.trim()
        )
      : !!(formData.counterPartyId.trim() && formData.counterCountryCode.trim());

  /**
   * Handle input change and clear previous API errors
   * @param {any} name - Input name
   * @param {any} value - Input value
   */
  const handleInputChange = (name: string, value: string) => {
    setApiError(null);
    let formatted = value;
    if (name === 'counterPartyId') {
      formatted = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 3);
    } else if (name === 'counterCountryCode') {
      formatted = value.trim().toUpperCase();
    }

    const updatedFormData = { ...formData, [name]: formatted };
    setFormData(updatedFormData);

    if (touched[name] || formatted) {
      setTouched((previous) => ({ ...previous, [name]: true }));
      const validationErrors = validateForm(updatedFormData, registrationType, t);
      setErrors((previous) => ({ ...previous, [name]: validationErrors[name] || '' }));
    }
  };

  /**
   * Handle input blur event to trigger validation
   * @param {any} name - Input name
   */
  const handleBlur = (name: string) => {
    setTouched((previous) => ({ ...previous, [name]: true }));
    const validationErrors = validateForm(formData, registrationType, t);
    setErrors((previous) => ({ ...previous, [name]: validationErrors[name] || '' }));
  };

  /**
   * Handle registration mode change
   * @param {any} mode - Registration mode ('SENDER' or 'RECEIVER')
   */
  const handleModeChange = (mode: 'SENDER' | 'RECEIVER') => {
    setRegistrationType(mode);
    setErrors({});
    setTouched({});
    setApiError(null);
  };

  /**
   * Handle copy
   * @param {any} text - Text to copy
   * @param {any} label - Label
   * @param {any} setCopied - State setter callback
   */
  const handleCopy = (
    text: string,
    label: string,
    setCopied: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    navigator.clipboard.writeText(text);

    setCopied(true);

    setTimeout(() => {
      setCopied(false);
    }, 1000);

    dispatch(
      setNotificationMessage({
        message: t('copiedToClipboard', { label }),
        type: 'success',
      }),
    );
  };

  /**
   * Handle submit
   */
  const handleSubmit = async () => {
    setApiError(null);
    const validationErrors = validateForm(formData, registrationType, t);

    const allTouched: Record<string, boolean> = {
      counterPartyId: true,
      counterCountryCode: true,
      counterRole: true,
    };
    if (registrationType === 'SENDER') {
      allTouched.theirVersionsUrl = true;
      allTouched.theirTokenA = true;
    }
    setTouched(allTouched);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      dispatch(
        setNotificationMessage({
          message: t('resolveFieldErrors'),
          type: 'error',
        }),
      );
      return;
    }

    setLoading(true);
    try {
      if (registrationType === 'SENDER') {
        await ocpiApi.registerAsSender({
          ...formData,
          ourPartyId,
          ourRole,
        });
        dispatch(setNotificationMessage({ message: t('handshakeSuccessful'), type: 'success' }));
        onSuccess();
        onClose();
      } else {
        const response = await ocpiApi.generateTokenA({
          ourPartyId,
          ourCountryCode,
          ourRole,
          counterPartyId: formData.counterPartyId,
          counterCountryCode: formData.counterCountryCode,
          counterRole: formData.counterRole,
        });

        // Since it's wrapped in Axios, data is usually inside response.data
        const responseData = response.data?.data || response.data || response;

        setGeneratedData({
          tokenA: responseData.tokenA || 'ERROR_GENERATING_TOKEN',
          versionsUrl: responseData.versionsUrl || 'ERROR_GENERATING_URL',
        });
        dispatch(
          setNotificationMessage({ message: t('invitationTokenGenerated'), type: 'success' }),
        );
        onSuccess();
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, t('operationFailed'));
      setApiError(errorMessage);

      const lowerMessage = errorMessage.toLowerCase();
      if (
        lowerMessage.includes('token') ||
        lowerMessage.includes('credential') ||
        lowerMessage.includes('unauthorized') ||
        lowerMessage.includes('match') ||
        lowerMessage.includes('invalid') ||
        lowerMessage.includes('401')
      ) {
        setErrors((previous) => ({
          ...previous,
          theirTokenA:
            lowerMessage.includes('token') ||
            lowerMessage.includes('credential') ||
            lowerMessage.includes('match') ||
            lowerMessage.includes('unauthorized')
              ? t('tokenAMismatchError')
              : previous.theirTokenA,
          theirVersionsUrl:
            lowerMessage.includes('url') || lowerMessage.includes('connect')
              ? t('unreachableVersionsUrlError')
              : previous.theirVersionsUrl,
        }));
        setTouched((previous) => ({
          ...previous,
          theirTokenA: true,
          theirVersionsUrl: true,
        }));
      }

      dispatch(
        setNotificationMessage({
          message: errorMessage,
          type: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Render form content
   * @returns {any} Rendered element
   */
  const renderFormContent = () => {
    let buttonLabel = t('generateTokenA');
    if (loading) {
      buttonLabel = t('processing');
    } else if (registrationType === 'SENDER') {
      buttonLabel = t('connectPartner');
    }

    if (generatedData) {
      return (
        <div className="rpm-token-result">
          <p className="rpm-token-result__desc">{t('shareHandshakeDetails')}</p>
          <div className="rpm-token-result__box">
            <div className="rpm-token-result__row">
              <span className="rpm-token-result__label">{t('versionsUrl')}</span>
              <div className="rpm-token-result__value-wrap">
                <code className="rpm-token-result__code">{generatedData.versionsUrl}</code>
                <MyButton
                  label={isVersionURLCopied ? t('copied') : t('copy')}
                  buttonType="outline"
                  onClickFunc={() =>
                    handleCopy(generatedData.versionsUrl, t('versionsUrl'), setIsVersionURLCopied)
                  }
                />
              </div>
            </div>
            <div className="rpm-token-result__row">
              <span className="rpm-token-result__label">{t('tokenA')}</span>
              <div className="rpm-token-result__value-wrap">
                <code className="rpm-token-result__code">{generatedData.tokenA}</code>
                <MyButton
                  label={isTokenACopied ? t('copied') : t('copy')}
                  buttonType="outline"
                  onClickFunc={() =>
                    handleCopy(generatedData.tokenA, t('tokenA'), setIsTokenACopied)
                  }
                />
              </div>
            </div>
          </div>
          <p className="rpm-token-result__note">{t('temporaryTokenNote')}</p>
          <div className="rpm-token-result__actions">
            <MyButton label={t('close')} buttonType="primary" onClickFunc={onClose} />
          </div>
        </div>
      );
    }

    return (
      <div className="register-partner-form">
        {/* Mode toggle */}
        <div className="rpm-type-toggle">
          <button
            type="button"
            className={`rpm-type-btn${registrationType === 'SENDER' ? ' active' : ''}`}
            onClick={() => handleModeChange('SENDER')}>
            {t('iAmSender')}
          </button>
          <button
            type="button"
            className={`rpm-type-btn${registrationType === 'RECEIVER' ? ' active' : ''}`}
            onClick={() => handleModeChange('RECEIVER')}>
            {t('iAmReceiver')}
          </button>
        </div>

        {/* API Error / Credential mismatch banner */}
        {apiError && (
          <div className="rpm-error-banner" role="alert">
            <AlertIcon className="rpm-error-banner__icon" />
            <div className="rpm-error-banner__content">
              <span className="rpm-error-banner__title">{t('registrationError')}</span>
              <span className="rpm-error-banner__msg">{apiError}</span>
            </div>
          </div>
        )}

        {/* Partner details (always shown) */}
        <div className="rpm-section">
          <h3 className="rpm-section__title">{t('partnerDetails')}</h3>
          <div className="rpm-section__grid">
            <CustomInput
              inputLabel={t('counterPartyId')}
              type="text"
              placeholder={t('counterPartyIdPlaceholder')}
              name="counterPartyId"
              onChangeValue={(_, value) => handleInputChange('counterPartyId', value as string)}
              handleBlur={() => handleBlur('counterPartyId')}
              value={formData.counterPartyId}
              characterLimit={3}
              error={Boolean(touched.counterPartyId && errors.counterPartyId)}
              errorMessage={touched.counterPartyId ? errors.counterPartyId : ''}
            />
            <SelectField
              inputLabel={t('countryCode')}
              name="counterCountryCode"
              placeholder={t('countryCodeSelectPlaceholder')}
              options={ISO_COUNTRY_CODES}
              selectedValue={formData.counterCountryCode}
              enableSearch
              onChangeValue={(name, value) => handleInputChange(name, value as string)}
              error={Boolean(touched.counterCountryCode && errors.counterCountryCode)}
              errorMessage={touched.counterCountryCode ? errors.counterCountryCode : ''}
            />
          </div>
          <div className="rpm-section__field">
            <SelectField
              inputLabel={t('partnerRole')}
              name="counterRole"
              options={[
                { label: 'CPO', value: 'CPO' },
                { label: 'eMSP', value: 'EMSP' },
              ].filter((opt) => opt.value.toUpperCase() !== ourRole.toUpperCase())}
              selectedValue={formData.counterRole}
              onChangeValue={(name, value) => handleInputChange(name, value as string)}
              error={Boolean(touched.counterRole && errors.counterRole)}
              errorMessage={touched.counterRole ? errors.counterRole : ''}
            />
          </div>
        </div>

        {/* Sender-specific: Handshake Credentials */}
        {registrationType === 'SENDER' && (
          <div className="rpm-mode-section rpm-mode-section--sender">
            <h3 className="rpm-mode-section__title">{t('handshakeCredentials')}</h3>
            <p className="rpm-mode-section__desc">{t('pasteHandshakeCredentials')}</p>
            <div className="rpm-section__field">
              <CustomInput
                inputLabel={t('partnerVersionsUrl')}
                type="text"
                placeholder={t('partnerVersionsUrlPlaceholder')}
                name="theirVersionsUrl"
                onChangeValue={(_, value) => handleInputChange('theirVersionsUrl', value as string)}
                handleBlur={() => handleBlur('theirVersionsUrl')}
                value={formData.theirVersionsUrl}
                error={Boolean(touched.theirVersionsUrl && errors.theirVersionsUrl)}
                errorMessage={touched.theirVersionsUrl ? errors.theirVersionsUrl : ''}
              />
            </div>
            <div className="rpm-section__field">
              <CustomInput
                inputLabel={t('partnerTokenA')}
                type="text"
                placeholder={t('partnerTokenAPlaceholder')}
                name="theirTokenA"
                onChangeValue={(_, value) => handleInputChange('theirTokenA', value as string)}
                handleBlur={() => handleBlur('theirTokenA')}
                value={formData.theirTokenA}
                error={Boolean(touched.theirTokenA && errors.theirTokenA)}
                errorMessage={touched.theirTokenA ? errors.theirTokenA : ''}
              />
            </div>
          </div>
        )}

        {/* Receiver-specific: Generate Invitation info */}
        {registrationType === 'RECEIVER' && (
          <div className="rpm-mode-section rpm-mode-section--receiver">
            <h3 className="rpm-mode-section__title">{t('generateInvitation')}</h3>
            <p className="rpm-mode-section__desc">
              {t('youAreGeneratingInvitation')} <strong>{t('tokenA')}</strong> {t('andA')}{' '}
              <strong>{t('versionsUrl')}</strong>. {t('shareWithPartner')}
            </p>
          </div>
        )}

        <div className="rpm-form-actions">
          <MyButton label={t('cancel')} buttonType="outline" onClickFunc={onClose} />
          <MyButton
            label={buttonLabel}
            buttonType="primary"
            onClickFunc={handleSubmit}
            disableButton={loading || !isFormValid}
          />
        </div>
      </div>
    );
  };

  return (
    <ModalBox
      open={open}
      title={t('establishOcpiConnection')}
      content={renderFormContent()}
      closeModal={onClose}
      header
      rightCorner=""
    />
  );
}

export default RegisterPartnerModal;
